import { ArrowLeftRight, Calendar, ChartColumn, ListTodo } from "lucide-react";

import { SEGMENT_TOUR_TARGET } from "./constants";

import type { TourDefinition } from "@/features/tours/types";

export const TOUR_STEP_ID = {
  VIEW_OR_EDIT: "view_or_edit",
  TABS: "tabs",
  COMBINE_GROUPS: "combine_groups",
  MATCH_IN_RULE: "match_in_rule",
  CONDITIONS: "conditions",
  CONDITION_EDITOR: "condition_editor",
  FIELD: "field",
  OPERATORS: "operators",
  VALUES: "values",
  EXCLUDE: "exclude",
  LIVE_COUNT: "live_count",
  EXCEPTIONS: "exceptions",
  SAVE: "save",
  ARCHIVE: "archive",
  VACANCIES: "vacancies",
  VACANCY_SEARCH: "vacancy_search",
  VACANCY_FILTERS: "vacancy_filters",
  VACANCY_LISTS: "vacancy_lists",
  VACANCY_FLIP: "vacancy_flip",
  HISTORY_TIMELINE: "history_timeline",
} as const;

export type TourStepId = (typeof TOUR_STEP_ID)[keyof typeof TOUR_STEP_ID];

export const TOUR_SECTION = {
  BUILDER: "builder",
  RULES: "rules",
  VACANCIES: "vacancies",
  HISTORY: "history",
} as const;

export const SEGMENT_BUILDER_TOUR: TourDefinition<TourStepId> = {
  id: "segment_builder",
  copyKey: "admin.segments.tour",
  steps: [
    { id: TOUR_STEP_ID.VIEW_OR_EDIT, section: TOUR_SECTION.BUILDER, target: SEGMENT_TOUR_TARGET.MODE_SWITCH, placement: "bottom-end" },
    { id: TOUR_STEP_ID.TABS, section: TOUR_SECTION.BUILDER, target: SEGMENT_TOUR_TARGET.TABS, placement: "bottom-start" },
    { id: TOUR_STEP_ID.LIVE_COUNT, section: TOUR_SECTION.BUILDER, target: SEGMENT_TOUR_TARGET.COUNT, placement: "bottom-end" },
    { id: TOUR_STEP_ID.EXCEPTIONS, section: TOUR_SECTION.BUILDER, target: SEGMENT_TOUR_TARGET.EXCEPTIONS, placement: "top" },
    { id: TOUR_STEP_ID.SAVE, section: TOUR_SECTION.BUILDER, target: SEGMENT_TOUR_TARGET.SAVE, placement: "top-end", isFixed: true },
    { id: TOUR_STEP_ID.ARCHIVE, section: TOUR_SECTION.BUILDER, target: SEGMENT_TOUR_TARGET.ARCHIVE, placement: "top-start", isFixed: true },
    { id: TOUR_STEP_ID.COMBINE_GROUPS, section: TOUR_SECTION.RULES, target: SEGMENT_TOUR_TARGET.COMBINE, placement: "bottom-end" },
    { id: TOUR_STEP_ID.MATCH_IN_RULE, section: TOUR_SECTION.RULES, target: SEGMENT_TOUR_TARGET.MATCH, placement: "bottom" },
    { id: TOUR_STEP_ID.CONDITIONS, section: TOUR_SECTION.RULES, target: SEGMENT_TOUR_TARGET.CONDITIONS, placement: "bottom" },
    { id: TOUR_STEP_ID.CONDITION_EDITOR, section: TOUR_SECTION.RULES, target: SEGMENT_TOUR_TARGET.CONDITION_EDITOR, placement: "top" },
    { id: TOUR_STEP_ID.FIELD, section: TOUR_SECTION.RULES, target: SEGMENT_TOUR_TARGET.FIELD_LIST, placement: "right", isMovingTarget: true },
    { id: TOUR_STEP_ID.OPERATORS, section: TOUR_SECTION.RULES, target: SEGMENT_TOUR_TARGET.OPERATOR_LIST, placement: "left", isMovingTarget: true },
    { id: TOUR_STEP_ID.VALUES, section: TOUR_SECTION.RULES, target: SEGMENT_TOUR_TARGET.VALUE_LIST, placement: "left", isMovingTarget: true },
    { id: TOUR_STEP_ID.EXCLUDE, section: TOUR_SECTION.RULES, target: SEGMENT_TOUR_TARGET.EXCLUDE, placement: "top" },
    { id: TOUR_STEP_ID.VACANCIES, section: TOUR_SECTION.VACANCIES, target: SEGMENT_TOUR_TARGET.VACANCIES, placement: "bottom-start" },
    { id: TOUR_STEP_ID.VACANCY_SEARCH, section: TOUR_SECTION.VACANCIES, target: SEGMENT_TOUR_TARGET.VACANCY_SEARCH, placement: "bottom-start" },
    { id: TOUR_STEP_ID.VACANCY_FILTERS, section: TOUR_SECTION.VACANCIES, target: SEGMENT_TOUR_TARGET.VACANCY_FILTERS, placement: "bottom-end" },
    { id: TOUR_STEP_ID.VACANCY_LISTS, section: TOUR_SECTION.VACANCIES, target: SEGMENT_TOUR_TARGET.VACANCY_LISTS, placement: "bottom" },
    { id: TOUR_STEP_ID.VACANCY_FLIP, section: TOUR_SECTION.VACANCIES, target: SEGMENT_TOUR_TARGET.VACANCY_FLIP, placement: "left" },
    { id: TOUR_STEP_ID.HISTORY_TIMELINE, section: TOUR_SECTION.HISTORY, target: SEGMENT_TOUR_TARGET.HISTORY, placement: "bottom-start" },
  ],
  sections: Object.values(TOUR_SECTION),
  finishPlacement: "bottom-end",
  welcomeFeatures: [
    { key: "feature1", Icon: ListTodo, fullDescriptionStep: TOUR_STEP_ID.SAVE },
    { key: "feature2", Icon: ChartColumn },
    { key: "feature3", Icon: ArrowLeftRight, requiresStep: TOUR_STEP_ID.VIEW_OR_EDIT },
    { key: "feature4", Icon: Calendar },
  ],
};

export const CONDITION_EDITOR_STEP_IDS: ReadonlySet<string> = new Set([
  TOUR_STEP_ID.CONDITION_EDITOR,
  TOUR_STEP_ID.FIELD,
  TOUR_STEP_ID.OPERATORS,
  TOUR_STEP_ID.VALUES,
  TOUR_STEP_ID.EXCLUDE,
]);

const EDIT_MODE_STEP_IDS: ReadonlySet<string> = new Set([
  ...CONDITION_EDITOR_STEP_IDS,
  TOUR_STEP_ID.SAVE,
  TOUR_STEP_ID.ARCHIVE,
  TOUR_STEP_ID.VACANCY_FLIP,
]);

const RULE_TARGET_STEP_IDS: ReadonlySet<string> = new Set([
  TOUR_STEP_ID.LIVE_COUNT,
  TOUR_STEP_ID.MATCH_IN_RULE,
  TOUR_STEP_ID.CONDITIONS,
]);

type TourStepAvailability = {
  hasModeSwitch: boolean;
  hasHistory: boolean;
  canEdit: boolean;
  hasArchive: boolean;
  hasSavedRules: boolean;
};

export function getTourStepIds({ hasModeSwitch, hasHistory, canEdit, hasArchive, hasSavedRules }: TourStepAvailability): TourStepId[] {
  return SEGMENT_BUILDER_TOUR.steps
    .filter(({ id, section }) => {
      if (id === TOUR_STEP_ID.VIEW_OR_EDIT) return hasModeSwitch;
      if (id === TOUR_STEP_ID.ARCHIVE) return hasArchive;
      if (id === TOUR_STEP_ID.LIVE_COUNT) return canEdit || hasSavedRules;
      if (section === TOUR_SECTION.RULES) return canEdit;
      if (EDIT_MODE_STEP_IDS.has(id)) return canEdit;
      if (section === TOUR_SECTION.HISTORY) return hasHistory;
      return true;
    })
    .map(({ id }) => id);
}

const EDIT_MODE_AND_RULE_TARGET_STEP_IDS: ReadonlySet<string> = new Set([...EDIT_MODE_STEP_IDS, ...RULE_TARGET_STEP_IDS]);

export function getEditModeStepIds({ hasRuleToShow }: { hasRuleToShow: boolean }) {
  return hasRuleToShow ? EDIT_MODE_STEP_IDS : EDIT_MODE_AND_RULE_TARGET_STEP_IDS;
}
